import { createSelector } from 'reselect';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { memoize } from 'lodash';
import Immutable from 'seamless-immutable';
import pathOr from 'ramda/src/pathOr';
import values from 'ramda/src/values';
import i18n from 'invision-core/src/components/i18n/i18n';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { MetadataCodeTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { OFFER_STATUS_INDICATOR_STATUS } from 'invision-core/src/constants/status.constants';
import { PRODUCT_CLASSIFICATIONS } from 'invision-core/src/constants/product.constants';
import CustomerCareKeys from '../../locales/keys';
import { CurrentCustomerSelector, SelectedCustomerSelector } from './customer.selectors';
import { addressStateRegionProvinceValueOptionsForCountry } from '../helpers/customer.selectors.helpers';
import { getResponsiblePartyName, transformRedirectablesFromExistingRedirects, transformRedirectablesFromOptions } from './customer.account.hierarchy.selectors.helper';
import { OFFERING_OPTION_STATUSES } from '../../components/shared/constants/offering.option.status.constants';
export var STANDALONE_TYPES = {
  AD_HOC: 'AD_HOC'
};

// "Selected Customer's Account Hierarchy selector"
var AccountHierarchySelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.accountHierarchy;
});
export var HierarchyNameSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return pathOr(null, ['data', 'hierarchyName'], accountHierarchy);
});
var NodesMapByIdSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.nodesMapById;
});
var NodesMapBySubscriberIdSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.nodesMapBySubscriberId;
});
export var CustomerSearchMapSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.customersSearch;
});
export var CustomerSearchMutableArraySelector = createSelector([CustomerSearchMapSelector], function (customerSearchMap) {
  return customerSearchMap ? values(customerSearchMap.asMutable({
    deep: true
  })) : null;
});
export var CustomerSearchRecordCountSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.customersSearchRecordCount;
});
export var CustomerSearchCurrentPageNumberSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.customersSearchPageNumber;
});
export var CustomerSearchTotalPageCountSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.customersSearchPageCount;
});
export var CustomerSearchIsSearchingCustomersSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isSearchingCustomers;
});

//broken here
export var CurrentCustomerNodeSelector = createSelector([CurrentCustomerSelector, NodesMapBySubscriberIdSelector], function (currentCustomer, nodesMapBySubscriberId) {
  if (currentCustomer && nodesMapBySubscriberId) {
    return nodesMapBySubscriberId[currentCustomer.Id];
  }
  return null;
});
// "Selected Customer's Account Hierarchy's Selected Node's Id selector"
export var SelectedNodeIdSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.selectedNodeId;
});
export var NodeToBeMoveIdSelector = createImmutableSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.nodeToBeMoved;
});
export var TargetNodeSelector = createImmutableSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.targetNode;
});

// "Selected Customer's Account Hierarchy's Selected Node selector"
export var SelectedNodeSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  var nodesMapById = accountHierarchy.data.nodesMapById;
  var selectedNodeId = accountHierarchy.selectedNodeId;
  return nodesMapById && selectedNodeId ? nodesMapById[selectedNodeId] : null;
});
export var SelectedNodeResponsiblePartyNameSelector = createSelector([SelectedNodeSelector], function (selectedNode) {
  if (selectedNode && selectedNode.ResponsiblePartySubscriber) {
    return getResponsiblePartyName(selectedNode.ResponsiblePartySubscriber);
  }
  return null;
});
export var IsCreatingHierarchySelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isCreatingHierarchy;
});

// "Selected Customer's Account Hierarchy's Selected Node's Subscriber Summary selector"
export var SelectedNodeSubscriberSummarySelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.selectedNodeSubscriberSummary;
});

//This will return the postpaid account details for the account, which, according to the API team, will always be the ONLY postpaid account on a subscriber
export var SelectedNodeSubscriberSummaryAccountDetailsSelector = createSelector([SelectedNodeSubscriberSummarySelector], function (summary) {
  var accountSummaries = pathOr([], ['AccountSummaries'], summary);
  var accountSummary = accountSummaries.find(function (accountSummary) {
    return !!accountSummary.PostpaidDetails;
  });
  return accountSummary ? accountSummary.PostpaidDetails : null;
});

//This is the selector for the node that currently has a popup open.
export var PopupHierarchyNodeSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.popupHierarchyNode;
});

// This is the subscriber summary for the node who's menu item has been selected (and opened up a popup)
export var PopupNodeSubscriberSummarySelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.popupNodeSubscriberSummary;
});

//This will return the results of the ConvergentBillerAccountDetails API call for the subscriber node with a popup that is currently open
export var PopupNodeConvergentBillerAccountDetailsSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.popupNodeConvergentBillerAccountDetails;
});
var EMPTY_REDIRECTABLES = Immutable([]);
export var RedirectablesSelector = createSelector([PopupNodeConvergentBillerAccountDetailsSelector, PopupNodeSubscriberSummarySelector], function (accountDetails, subscriberSummary) {
  var redirectables;
  var accountSummaries = pathOr([], ['AccountSummaries'], subscriberSummary);
  var offersAndStandalones = pathOr([], ['Offerings'], accountDetails);
  var accountDetailsAdhocs = pathOr([], ['Standalones'], accountDetails).filter(function (standalone) {
    return standalone.ProductClassification === PRODUCT_CLASSIFICATIONS.AD_HOC;
  });
  if (accountDetailsAdhocs.length) {
    offersAndStandalones = offersAndStandalones.concat({
      DisplayName: i18n.translate(CustomerCareKeys.AD_HOC),
      OfferingInstanceId: STANDALONE_TYPES.AD_HOC,
      Options: accountDetailsAdhocs
    });
  }
  offersAndStandalones = offersAndStandalones.map(function (offer) {
    return Object.assign({}, offer, {
      Options: offer.Options.filter(function (option) {
        return pathOr(0, ['BillerTypeAmounts', 'length'], option); //Remove items that don't have BillerTypeAmounts as they are no longer being charged for.
      })
    });
  });
  if (accountSummaries.length && offersAndStandalones.length) {
    // NOTE: This assumes there's only 1 postpaid account for a subscriber
    var postpaidAccount = accountSummaries.find(function (accountSummary) {
      return !!accountSummary.PostpaidDetails;
    });
    if (postpaidAccount) {
      redirectables = offersAndStandalones.filter(function (offer) {
        // Ensure an offer is not "removed" and has at least 1 option that is "active", "pending active", or "pending removed"
        return offer.Status !== OFFER_STATUS_INDICATOR_STATUS.REMOVED && offer.Options.some(function (option) {
          return option.AccountNumber === postpaidAccount.AccountNumber && (option.Status === OFFERING_OPTION_STATUSES.ACTIVE || option.Status === OFFERING_OPTION_STATUSES.PENDING_ACTIVE || option.Status === OFFERING_OPTION_STATUSES.PENDING_REMOVED);
        });
      });
    }
  }
  return redirectables ? Immutable(redirectables) : EMPTY_REDIRECTABLES;
});
var EMPTY_REDIRECTABLES_VIEW_MODEL = Immutable([]);
export var RedirectablesViewModelSelector = createSelector([AccountHierarchySelector, CurrentCustomerSelector, NodesMapBySubscriberIdSelector, RedirectablesSelector, PopupHierarchyNodeSelector], function (accountHierarchy, currentCustomer, nodesMapBySubscriberId, redirectables, popupHierarchyNode) {
  var chargeRedirectInstances = pathOr([], ['data', 'popupNodeRedirectedServices', 'ChargeRedirectInstances'], accountHierarchy);
  var redirectablesWithTransformations = transformRedirectablesFromOptions(redirectables);

  // Map existing redirects to current offers/options to indicate which ones already exist, who they're redirected to, etc
  if (chargeRedirectInstances.length) {
    // Restrict existing redirects to those for the current subscriber's services
    var redirectedServices = chargeRedirectInstances.filter(function (service) {
      return service.SourceSubscriber.Id === popupHierarchyNode.SubscriberId;
    });
    redirectablesWithTransformations = transformRedirectablesFromExistingRedirects(redirectablesWithTransformations, redirectedServices, nodesMapBySubscriberId, currentCustomer.Id);
  }
  return !redirectablesWithTransformations.length ? EMPTY_REDIRECTABLES_VIEW_MODEL : redirectablesWithTransformations.map(function (offer) {
    var uniqueOptions = {};
    return offer.merge({
      Options: offer.Options.map(function (option) {
        // Prevent duplicate instances of an option from being displayed
        var isHidden = !!uniqueOptions[option.ProductClassification === PRODUCT_CLASSIFICATIONS.AD_HOC ? option.ProductId : option.OfferingOptionPriceId];
        if (!isHidden) {
          uniqueOptions[option.ProductClassification === PRODUCT_CLASSIFICATIONS.AD_HOC ? option.ProductId : option.OfferingOptionPriceId] = option;
        }
        return option.merge({
          isDisplaySelected: offer.Options.some(function (offerOption) {
            return offerOption.isSelected && (option.ProductClassification === PRODUCT_CLASSIFICATIONS.AD_HOC ? option.ProductId === offerOption.ProductId : option.OfferingOptionPriceId === offerOption.OfferingOptionPriceId);
          }),
          isHidden: isHidden
        });
      })
    });
  });
});
export var HierarchyNodeSelector = createSelector([NodesMapByIdSelector], function (nodesMapById) {
  return memoize(function (nodeId) {
    return nodesMapById && nodeId ? nodesMapById[nodeId] : null;
  });
});
export var HierarchyNodeBySubscriberSelector = createSelector([NodesMapBySubscriberIdSelector], function (nodesMapBySubscriberId) {
  return memoize(function (subscriberId) {
    return nodesMapBySubscriberId && subscriberId ? nodesMapBySubscriberId[subscriberId] : null;
  });
});
export var RootNodeIdSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.data.rootNodeId;
});
export var NodeIsInPathToContextSelector = createSelector([RootNodeIdSelector, NodesMapBySubscriberIdSelector, SelectedCustomerSelector], function (rootNodeId, nodesMapBySubscriberId, selectedCustomer) {
  var currentSubscriberNode = nodesMapBySubscriberId && nodesMapBySubscriberId[selectedCustomer.data.Id];
  return memoize(function (nodeId) {
    return currentSubscriberNode && checkNode(currentSubscriberNode);
    function checkNode(nodeToCheck) {
      return nodeToCheck.Id === nodeId || nodeToCheck.Id !== rootNodeId && checkNode(nodesMapBySubscriberId[nodeToCheck.ParentSubscriberId]);
    }
  });
});

// "Selected Customer's Account Hierarchy-related error selector"
export var ErrorSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.accountHierarchyError;
});
export var LastAttemptErrorSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.lastAttemptError;
});
export var CreateHierarchyErrorSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.createHierarchyError;
});

// "Selected Customer's Account Hierarchy is being retrieved selector"
export var IsRetrievingHierarchySelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isRetrievingHierarchy;
});
export var IsUpdatingHierarchyNodeSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isUpdatingHierarchyNode;
});
export var IsUpdatingHierarchyNodeServiceSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isUpdatingNodeService;
});

// "Selected Customer's Account Hierarchy's (Selected Node's) Subscriber Summary is being retrieved selector"
export var IsRetrievingNodeSubscriberSummarySelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isRetrievingNodeSubscriberSummary;
});
export var IsRetrievingPopupNodeConvergentBillerAccountDetailsSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isRetrievingPopupNodeConvergentBillerAccountDetails;
});
export var ResponsiblePartyCustomerSelector = createSelector([CurrentCustomerSelector, HierarchyNodeBySubscriberSelector], function (currentCustomer, hierarchyNodeBySubscriber) {
  var hierarchyNode = hierarchyNodeBySubscriber(currentCustomer.Id);
  return hierarchyNode && hierarchyNode.ResponsiblePartySubscriber;
});
export var ResponsiblePartyCustomerNameSelector = createSelector([ResponsiblePartyCustomerSelector], function (responsiblePartyCustomer) {
  if (responsiblePartyCustomer) {
    return getResponsiblePartyName(responsiblePartyCustomer);
  }
  return null;
});
export var CreateChildModelSelector = createSelector([AccountHierarchySelector, CurrentCustomerSelector], function (accountHierarchy, currentCustomer) {
  return accountHierarchy.createChildModel.merge({
    Customer: {
      HomeCountry: pathOr(null, ['createChildModel', 'Customer', 'HomeCountry'], accountHierarchy) || pathOr(null, ['HomeCountry'], currentCustomer),
      SubscriberCurrency: pathOr(null, ['createChildModel', 'Customer', 'SubscriberCurrency'], accountHierarchy) || pathOr(null, ['SubscriberCurrency'], currentCustomer)
    }
  }, {
    deep: true
  }).asMutable({
    deep: true
  });
});
export var CreateChildStateRegionProvinceValueOptions = createSelector([CreateChildModelSelector, MetadataCodeTypeSelector(CODES.AddressCountry), MetadataCodeTypeSelector(CODES.AddressStateProvinceRegion)], function (createChildModel, countryCodes, stateCodes) {
  return addressStateRegionProvinceValueOptionsForCountry(createChildModel.Customer.HomeCountry, countryCodes, stateCodes);
});
export var IsCreatingChildSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isCreatingChild;
});
export var CreateChildErrorSelector = createSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.createChildError;
});
export var IsChildForCustomerHierarchySelector = createImmutableSelector([AccountHierarchySelector], function (accountHierarchy) {
  return accountHierarchy.isChildForCustomerHierarchy;
});